import { useStateWithRef } from "@kalyzee/kast-web-sdk/web-components";
import { ReactElement, useEffect, useRef } from "react";
import { MessagingBridgeManagerMaster, MessagingBridgeManagerMasterOptions } from "../helpers/bridge/master";
import { MessagingBridgeManagerSlave, MessagingBridgeManagerSlaveOptions } from "../helpers/bridge/slave";
import { Debug } from "../helpers/debug";
import { MessagingBridgeContext } from "./messageBridgeManager";

// ----------------------------------------- //
// ------------  MASTER --------------------- //
// ----------------------------------------- //

export interface MessagingBridgeManagerMasterContextProviderProps {
  options?: MessagingBridgeManagerMasterOptions;
  children?: ReactElement;
  onMessagingBridgeManager?: (manager: MessagingBridgeManagerMaster) => void;
}

export const MessagingBridgeManagerMasterContextProvider = ({
  options,
  children,
  onMessagingBridgeManager,
}: MessagingBridgeManagerMasterContextProviderProps) => {
  const [manager, setManager, managerRef] = useStateWithRef<MessagingBridgeManagerMaster>();
  const onBridgeRef = useRef(onMessagingBridgeManager);

  useEffect(() => {
    onBridgeRef.current = onMessagingBridgeManager;
  }, [onMessagingBridgeManager]);

  useEffect(() => {
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const manager = new MessagingBridgeManagerMaster(options, managerRef.current?.getClients());
    if (Debug.isEnabled()) {
      const debugData = Debug.getData();
      if (debugData) debugData.messagingBridgeManager = manager;
    }
    onBridgeRef.current?.(manager);
    setManager(manager);
    return () => {
      manager.destroy();
    };
  }, [options]);

  return <MessagingBridgeContext.Provider value={manager}>{children}</MessagingBridgeContext.Provider>;
};

// ----------------------------------------- //
// ------------  SLAVE --------------------- //
// ----------------------------------------- //

export interface MessagingBridgeManagerSlaveContextProviderProps {
  options?: MessagingBridgeManagerSlaveOptions;
  children?: ReactElement;
  onMessagingBridgeManager?: (manager: MessagingBridgeManagerSlave) => void;
}

export const MessagingBridgeManagerSlaveContextProvider = ({
  options,
  children,
  onMessagingBridgeManager,
}: MessagingBridgeManagerSlaveContextProviderProps) => {
  const [manager, setManager] = useStateWithRef<MessagingBridgeManagerSlave>();
  const onBridgeRef = useRef(onMessagingBridgeManager);

  useEffect(() => {
    onBridgeRef.current = onMessagingBridgeManager;
  }, [onMessagingBridgeManager]);

  useEffect(() => {
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const manager = new MessagingBridgeManagerSlave(options);
    if (Debug.isEnabled()) {
      const debugData = Debug.getData();
      if (debugData) debugData.messagingBridgeManager = manager;
    }
    onBridgeRef.current?.(manager);
    setManager(manager);
    return () => {
      manager.destroy();
    };
  }, [options]);

  return <MessagingBridgeContext.Provider value={manager}>{children}</MessagingBridgeContext.Provider>;
};
